import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";

import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export const WhiteTemplate = ({
    children,
    productEditionText,
    productLogos,
    textBody,
    textHeading,
    valueProps,
    valuePropsHeading,
    useWhitewaveCopyUpdate
}: PropsWithChildren<{
    productEditionText?: string;
    productLogos?: ReactNode[];
    textBody: string;
    textHeading: string;
    valueProps: {
        icon: ReactNode;
        heading: string;
        body: string;
    }[];
    valuePropsHeading: string;
    useWhitewaveCopyUpdate?: boolean;
}>) => (
    <div css={containerCss}>
        <header css={headerCss}>
            <h1 css={productLogosCss}>{productLogos}</h1>
            {productEditionText && (
                <p css={productEditionTextCss}>{productEditionText}</p>
            )}
        </header>
        <main css={mainCss}>
            <div css={formColumnCss}>
                {children}
                <div css={atlassianLogoCss}>
                    <AtlassianLogo appearance="brand" size="xsmall" />
                </div>
            </div>
            <div css={contentColumnCss}>
                <h2 css={textHeadingCss}>{textHeading}</h2>
                <p css={textBodyCss}>{textBody}</p>
                <h3 css={valuePropsHeadingCss}>{valuePropsHeading}</h3>
                <ul css={valuePropsListCss}>
                    {valueProps.map(({ icon, heading, body }, index) => (
                        <li
                            css={[
                                valuePropItemCss,
                                useWhitewaveCopyUpdate && valuePropItemCssNew
                            ]}
                            key={index}
                        >
                            {icon}
                            <div>
                                <h4
                                    css={[
                                        valuePropItemHeadingCss,
                                        useWhitewaveCopyUpdate &&
                                            valuePropItemHeadingCssNew
                                    ]}
                                >
                                    {heading}
                                </h4>
                                <p
                                    css={[
                                        valuePropItemBodyCss,
                                        useWhitewaveCopyUpdate &&
                                            valuePropItemBodyCssNew
                                    ]}
                                >
                                    {body}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </main>
    </div>
);

const atlassianLogoCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockEnd: 0
});

const containerCss = css({
    display: "flex",
    flexDirection: "column",
    gap: 48,
    paddingBlockStart: 20
});

const contentColumnCss = css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [media.above.sm]: {
        paddingBlockStart: 64
    }
});

const formColumnCss = css({
    backgroundColor: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "column",
    margin: "0 auto",
    maxWidth: "100%",
    width: 414,
    justifyContent: "space-between",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ['button, div[role="presentation"]']: {
        borderRadius: `3px !important`,
        fontSize: 16
    },
    [media.above.xs]: {
        borderRadius: 8,
        boxShadow: token("elevation.shadow.overlay"),
        padding: 32
    }
});

const headerCss = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
});

const mainCss = css({
    display: "flex",
    flexDirection: "column",
    gap: 36,
    margin: "0 auto",
    padding: "0 24px",
    maxWidth: 926,
    width: "100%",
    [media.above.sm]: {
        flexDirection: "row-reverse !important"
    }
});

const productEditionTextCss = css({
    fontFamily: wacToken("font.charlie.display"),
    fontSize: 20,
    fontWeight: 400,
    margin: 0
});

const productLogosCss = css({
    margin: 0,
    padding: "0 16px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["> span, svg"]: {
        display: "block",
        maxWidth: "100%",
        margin: "0 auto"
    }
});

const textHeadingCss = css({
    fontSize: 20,
    margin: 0
});

const textBodyCss = css({
    margin: 0
});

const valuePropsHeadingCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: 16,
    margin: 0
});

const valuePropsListCss = css({
    listStyle: "none",
    margin: 0,
    padding: 0
});

const valuePropItemCss = css({
    display: "flex",
    alignItems: "center",
    marginBlockEnd: 12,
    gap: 16
});

const valuePropItemHeadingCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: 16,
    margin: 0
});

const valuePropItemBodyCss = css({
    margin: 0
});

const valuePropItemCssNew = css({
    display: "flex",
    alignItems: "flex-start",
    marginBlockEnd: 12,
    gap: 16
});

const valuePropItemHeadingCssNew = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: 16,
    margin: 0,
    marginBlockStart: "-7px"
});

const valuePropItemBodyCssNew = css({
    margin: 0,
    marginBlockStart: "-2px"
});
